import { ref } from 'vue';
import { defineStore } from 'pinia';

export type LayoutMode = 'top' | 'side';

export const LAYOUT_STORAGE_KEY = 'layoutMode';
export const LAYOUT_COLLAPSED_STORAGE_KEY = 'layoutCollapsed';

const DEFAULT_MODE: LayoutMode = 'top';

function readMode(): LayoutMode {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    return raw === 'side' || raw === 'top' ? raw : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
}

function writeMode(mode: LayoutMode): void {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, mode);
  } catch {
    // Private-mode / quota failures are non-fatal — the choice just won't persist.
  }
}

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(LAYOUT_COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(LAYOUT_COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    // Non-fatal — the choice just won't persist.
  }
}

export const useLayoutStore = defineStore('layout', () => {
  // Durable, per-browser preferences — hydrated from localStorage at creation.
  const mode = ref<LayoutMode>(readMode());
  // Only meaningful in 'side' mode: shrinks the ribbon to an icon-only rail.
  const collapsed = ref<boolean>(readCollapsed());

  function setMode(next: LayoutMode): void {
    mode.value = next;
    writeMode(next);
  }

  function toggle(): void {
    setMode(mode.value === 'top' ? 'side' : 'top');
  }

  function setCollapsed(next: boolean): void {
    collapsed.value = next;
    writeCollapsed(next);
  }

  function toggleCollapsed(): void {
    setCollapsed(!collapsed.value);
  }

  return { mode, collapsed, setMode, toggle, setCollapsed, toggleCollapsed };
});
